import { ChevronDownIcon, Clock01Icon } from '@/icons';
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  TimePicker,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useUpdateCompanyMutation } from '@/app/features/control-panel/organization/organization.api';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import GeoFenceMap from '@/components/GeoFanceMap';
import WithLoader from '@/components/WithLoader';
import { useTimeZone } from '@/hooks/useTimeZone';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';

type Props = CustomModalProps & { id?: string; data: any };

export default function ManageCompany({
  open,
  handleClose,
  data: companyDetails,
}: Props) {
  const [form] = Form.useForm();
  const radius = Form.useWatch('radius', form);
  const { options } = useTimeZone();
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();

  const handleSubmit = async (values: any) => {
    const {
      working_days,
      parent_office_id,
      default_shift_end_time,
      default_shift_start_time,
      ...rest
    } = values || {};
    try {
      const payload = {
        ...rest,
        working_days: working_days || [],
        parent_office_id: parent_office_id || undefined,
        default_shift_end_time: default_shift_end_time?.format('HH:mm'),
        default_shift_start_time: default_shift_start_time?.format('HH:mm'),
      };
      const response = await updateCompany(payload).unwrap();

      // Check if API response has success: false
      if (response && response.success === false) {
        toast('error', response.message || 'Failed to update company');
        return;
      }

      toast('success', 'Company updated successfully');
      handleClose();
    } catch (error: any) {
      const message = getErrorMessage(error);
      toast('error', message || 'Failed to update company');
    }
  };

  // Populate form with company details when data is loaded
  useEffect(() => {
    if (companyDetails?.data && open) {
      const { default_shift_end_time, default_shift_start_time, ...company } =
        companyDetails.data;
      form.setFieldsValue({
        ...company,
        default_shift_start_time: default_shift_start_time
          ? dayjs(default_shift_start_time, 'HH:mm')
          : null,
        default_shift_end_time: default_shift_end_time
          ? dayjs(default_shift_end_time, 'HH:mm')
          : null,
      });
    }
  }, [companyDetails, form, open]);

  const weekOptions = [
    { label: 'Sunday', value: 'Sunday' },
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' },
  ];

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={'Edit Company Details'}
    >
      <WithLoader isLoading={false}
        className="rounded-lg"
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Form.Item
              label="Company Name"
              name="company_name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="" />
            </Form.Item>
            <Form.Item
              label="Company Email"
              name="company_email"
              rules={[
                { required: true, message: 'Please enter company email' },
              ]}
            >
              <Input placeholder="" />
            </Form.Item>
            <div className="col-span-2">
              <Form.Item
                label="Admin Name"
                name="admin_name"
                rules={[{ required: true, message: 'Please enter admin name' }]}
              >
                <Input placeholder="" />
              </Form.Item>
            </div>

            <Form.Item
              label="Admin Email"
              name="admin_email"
              rules={[{ required: true, message: 'Please enter admin email' }]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>

            <Form.Item
              label="Admin Phone"
              name="admin_phone"
              rules={[{ required: true, message: 'Please enter admin phone' }]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Contact Person Name"
                name="contact_person_name"
                rules={[
                  {
                    required: true,
                    message: 'Please enter contact person name',
                  },
                ]}
              >
                <Input placeholder="" />
              </Form.Item>
            </div>

            <Form.Item
              label="Contact Person Email"
              name="contact_person_email"
              rules={[
                {
                  required: true,
                  message: 'Please enter contact person email',
                },
              ]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>

            <Form.Item
              label="Contact Person Phone"
              name="contact_person_phone"
              rules={[
                {
                  required: true,
                  message: 'Please enter contact person phone',
                },
              ]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>

            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: 'Please enter country' }]}
            >
              <Input placeholder="Write here..." />
            </Form.Item>

            <Form.Item
              label="Timezone"
              name="timezone"
              rules={[{ required: true, message: 'Please select timezone' }]}
            >
              <Select
                options={options}
                placeholder="Select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>

            <div className="col-span-2">
              <Form.Item
                label="Working Days"
                name="working_days"
                rules={[
                  { required: true, message: 'Please select at least one day' },
                ]}
              >
                <Checkbox.Group
                  options={weekOptions}
                  className="custom-check"
                />
              </Form.Item>
            </div>
            <div className="col-span-2">
              <GeoFenceMap
                defaultValues={
                  companyDetails?.data?.latitude &&
                  companyDetails?.data?.longitude
                    ? {
                        lat: parseFloat(companyDetails?.data?.latitude),
                        long: parseFloat(companyDetails?.data.longitude),
                      }
                    : undefined
                }
                onMapChange={(latitude, longitude) => {
                  form.setFieldsValue({
                    latitude: latitude.toString(),
                    longitude: longitude.toString(),
                  });
                }}
                radius={radius}
              />
            </div>
            <Form.Item
              label="Latitude"
              name="latitude"
              rules={[{ required: true, message: 'Please enter latitude' }]}
            >
              <InputNumber controls={false} className="w-full" placeholder="" />
            </Form.Item>

            <Form.Item
              label="Longitude"
              name="longitude"
              rules={[{ required: true, message: 'Please enter longitude' }]}
            >
              <InputNumber controls={false} className="w-full" placeholder="" />
            </Form.Item>

            <Form.Item
              label="Radius"
              name="radius"
              rules={[{ required: true, message: 'Please enter radius' }]}
            >
              <InputNumber
                controls={false}
                className="w-full"
                placeholder="00"
                suffix="Meters"
              />
            </Form.Item>

            <Form.Item
              label="Outside Fence Action"
              name="outside_fence_action"
              rules={[
                {
                  required: true,
                  message: 'Please select outside fence action',
                },
              ]}
            >
              <Select
                placeholder="Select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={[
                  { value: 'ALLOW_ATTENDANCE', label: 'Allow Attendance' },
                  { value: 'BLOCK_ATTENDANCE', label: 'Block Attendance' },
                ]}
              />
            </Form.Item>

            <div className="col-span-2">
              <div className="justify-start text-white text-2xl font-normal">
                Office Time
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Form.Item
                  label={'Default Shift Start Time'}
                  name="default_shift_start_time"
                  rules={[
                    { required: true, message: 'Please select start time' },
                  ]}
                >
                  <TimePicker
                    showNow={false}
                    needConfirm={false}
                    placeholder="HH/MM/SS"
                    suffixIcon={<Clock01Icon size={18} />}
                  />
                </Form.Item>
                <Form.Item
                  label={'Default Shift End Time'}
                  name="default_shift_end_time"
                  rules={[
                    { required: true, message: 'Please select end time' },
                  ]}
                >
                  <TimePicker
                    showNow={false}
                    needConfirm={false}
                    placeholder="HH/MM/SS"
                    suffixIcon={<Clock01Icon size={18} />}
                  />
                </Form.Item>
                <Form.Item
                  label={'Default Shift Check-in Grace Period'}
                  name="default_shift_check_in_grace_period"
                  rules={[
                    { required: true, message: 'Please enter grace period' },
                  ]}
                >
                  <InputNumber
                    controls={false}
                    className="w-full"
                    placeholder="15"
                    suffix="Minutes"
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          <Flex justify="end">
            <Button
              className="btn-primary min-w-[80px] h-11"
              htmlType="submit"
              loading={isLoading}
            >
              Save
            </Button>
          </Flex>
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
}
